import { CheckableColl } from 'src/models/CheckableColl'
import CheckableMap from 'src/models/CheckableMap'
import ExtendedMap from 'src/models/ExtendedMap'

import teacherApi from '@/api/teacher.api'
import { CURRENT_SERVICE_TYPE, MAX_REQUEST_NUMBER } from '@/constant/common'
import { LevelSetting, LevelSettingColl, Teacher, User } from '@/dto'
import { ReqParams } from '@/dto/common/ReqParams'

import { makeAdapter } from './Adapter'
import type Controller from './Controller'
import queryClient from './queryClient'

class TeacherController implements Controller {
  adapter = {
    teacher: makeAdapter({
      from(data: res.Teacher): Teacher {
        return new Teacher(data)
      },
      toCreate(data: Teacher): req.CreateTeacher {
        const { name, phoneNumber, loginId, type, serviceTypes } = data
        return {
          name,
          phoneNumber,
          loginId,
          type,
          serviceTypes: serviceTypes?.length ? serviceTypes : [CURRENT_SERVICE_TYPE],
        }
      },
      toModify(data: Teacher): req.PatchTeacher {
        const { teacherId, name, phoneNumber, type, serviceTypes } = data
        return { teacherId, name, phoneNumber, type, serviceTypes }
      },
      toId(data: Teacher): req.TeacherId {
        if (data.teacherId === undefined) {
          throw new Error('선생님 아이디가 없습니다.')
        }
        return data.teacherId
      },
    }),
    user: makeAdapter({
      from(teacher: res.Teacher): User {
        return new User({
          teacher,
        })
      },
    }),
    setting: makeAdapter({
      from({
        key,
        data,
      }: {
        key:
          | 'veryLow'
          // | 'low'
          | 'middle'
          | 'high'
        // | 'veryHigh'
        data: res.TeacherSetting['high']['difficultySetting']
      }) {
        return new LevelSetting(key, data)
      },
    }),
  }
  fetchHasTeachers = async () => {
    const {
      data: { content },
    } = await teacherApi.getTeachers(new ReqParams({ params: { size: 1, page: 0 } }).toQueryString)

    return content.length > 0
  }

  fetchTeachers = async (pageQueryString: string) => {
    const {
      data: { content, hasNext },
    } = await teacherApi.getTeachers(pageQueryString)

    return {
      teacherColl: new CheckableColl(
        CheckableMap.fromArr(content.map(this.adapter.teacher.from), 'teacherId' as keyof Teacher),
      ),
      last: !hasNext,
    }
  }

  // 전체 선생님 중
  fetchAllTeachers = () => {
    return queryClient.fetchQuery({
      queryKey: ['fetchAllTeachers'],
      queryFn: () =>
        this.fetchTeachers(
          new ReqParams({ params: { size: MAX_REQUEST_NUMBER, present: true } }).toQueryString,
        ),
    })
  }

  fetchUserTeacher = async () => {
    const teacherRes = await teacherApi.getOneTeacher()

    return this.adapter.user.from(teacherRes.data)
  }

  fetchTeacherSetting = async () => {
    const { data } = await teacherApi.getTeacherSetting()
    const levelSettingKeywords = ['veryLow', /* 'low', */ 'middle', 'high' /* 'veryHigh' */]

    const arr = Object.entries(data)
      .map(([key, value]) => {
        if (value && 'difficultySetting' in value && levelSettingKeywords.includes(key)) {
          return this.adapter.setting.from({
            key: key as 'veryLow' | /* 'low' | */ 'middle' | 'high' /* | 'veryHigh' */,
            data: value.difficultySetting,
          })
        }
      })
      .filter((v) => v)

    return {
      levelSetting: new LevelSettingColl(ExtendedMap.fromArr(arr as LevelSetting[], 'key')),
      worksheetSearchSetting: data.worksheetSearchSetting,
      workbookSearchSetting: data.workbookSearchSetting,
    }
  }

  fetchTeacherWorksheetFilterSetting = async () => {
    const { data } = await teacherApi.getTeacherSetting()

    return data
  }

  updateTeacherSetting = async (data: req.TeacherSetting) => {
    await teacherApi.putTeacherSetting(data)
  }

  createTeacher = async (teacher: Teacher) => {
    await teacherApi.postTeacher(this.adapter.teacher.toCreate(teacher))
    queryClient.invalidateQueries({ queryKey: ['fetchAllTeachers'] })
  }

  // [변경] 선생님 관리에서 특정 선생님 수정 → patchTeacherById (PATCH /teachers)
  modifyTeacher = async (teacher: Teacher) => {
    await teacherApi.patchTeacherById(this.adapter.teacher.toModify(teacher))
    queryClient.invalidateQueries({ queryKey: ['fetchAllTeachers'] })
  }

  // [변경] 마이페이지에서 내 정보 수정 → patchMyTeacher (PATCH /teachers/me)
  modifyMyTeacher = async (teacher: Teacher | User) => {
    const { name, phoneNumber, type, academyName } = teacher
    await teacherApi.patchMyTeacher({ name, phoneNumber, type, academyName })
  }

  deleteTeachers = async (teacherIds: req.TeacherId[]) => {
    await teacherApi.deleteTeachers(teacherIds)
    queryClient.invalidateQueries({ queryKey: ['fetchAllTeachers'] })
  }

  initPassword = async (teacher: Teacher) => {
    await teacherApi.resetTeacherPassword(this.adapter.teacher.toId(teacher))
  }

  fetchActivityCount = async () => {
    const { data } = await teacherApi.getActivityCount()
    return data
  }

  markLogin = async () => {
    const { data } = await teacherApi.markLogin()
    return data.firstLogin
  }
}

export const teacherController = new TeacherController()
